import { useEffect } from 'react';
import deepEqual from 'fast-deep-equal';
import { useMeContext } from '~/modules/me';
import { useEffectiveProjectGroupPolicy } from '~/modules/common/components/Groups/project';
import { useSessionStorage } from '~/modules/common/hooks';
import { useProjectTimesheetAccessQuery } from './useProjectTimesheetAccessQuery';

export const useProjectTimesheetAccess = projectId => {
  const {
    groupSettings: {
      costCenter: costCenterSettings,
      department: departmentSettings,
      division: divisionSettings,
      employeeType: employeeTypeSettings,
      location: locationSettings,
      serviceCenter: serviceCenterSettings
    },
    permissionsMap,
    legacyDepartmentDisabled,
    legacyEmployeeTypeDisabled
  } = useMeContext();

  const effectiveProjectGroupPolicy = useEffectiveProjectGroupPolicy();
  const variables = {
    projectId,
    taskAssignmentsFilter: { includeClosedTasks: false },
    includeLocationFullPath: Boolean(
      permissionsMap['urn:replicon:location-action:view-location-details']
    ),
    includeServiceCenterFullPath: Boolean(
      permissionsMap[
        'urn:replicon:service-center-action:view-service-center-details'
      ]
    ),
    includeDivisionFullPath: Boolean(
      permissionsMap['urn:replicon:division-action:view-division-details']
    ),
    includeCostCenterFullPath: Boolean(
      permissionsMap['urn:replicon:cost-center-action:view-cost-center-details']
    ),
    includeDepartmentFullPath: Boolean(
      permissionsMap['urn:replicon:department-action:view-department']
    ),
    includeEmployeeTypeFullPath: Boolean(
      permissionsMap['urn:replicon:employee-type-action:view-employee-type']
    ),
    policy: effectiveProjectGroupPolicy
  };

  const { storedValue, setValue } = useSessionStorage(
    'PROJECT-TIMESHEET-ACCESS-QUERY-VARIABLES',
    null
  );

  useEffect(() => {
    if (!deepEqual(storedValue, variables)) {
      setValue(variables);
    }
  }, [variables, setValue, storedValue]);

  const { data, loading } = useProjectTimesheetAccessQuery({
    variables,
    fetchPolicy: 'network-only'
  });

  const timesheetAccess = data && data.project && data.project.timesheetAccess;
  const billingType = data && data.project && data.project.billingType;

  return {
    billingType,
    costCenterSettings,
    departmentSettings,
    divisionSettings,
    employeeTypeSettings,
    locationSettings,
    serviceCenterSettings,
    legacyDepartmentDisabled,
    legacyEmployeeTypeDisabled,
    loading,
    timesheetAccess
  };
};
